/**
 * Frontend mirror of functions-forms/src/kb-assistant/selectQuestions.ts.
 * Keep both in sync — Cloud Functions cannot import from src/.
 */

import { HARD_RULES } from './compliancePolicy';
import {
  ESSENTIAL_ALWAYS_ASK_IDS,
  HELPFUL_ALWAYS_ASK_IDS,
  INDUSTRY_IDS,
  INDUSTRY_LABELS,
  INDUSTRY_SUPPORT,
  REGULATED_VERTICAL_CONFIRM_QUESTION,
  RESTRICTED_VERTICAL_NOTICE,
  getIndustryPack,
  isAlwaysAskId,
  isEssentialAlwaysAskId,
  isHelpfulAlwaysAskId,
  isRegulatedIndustryId,
  questionKind,
} from './industryPacks';

export const MIN_QUESTIONS = 6;
export const MAX_QUESTIONS = 16;
/** Pack-specific preference questions reserved before helpful always-ask compete. */
export const RESERVED_PREFERENCE_SLOTS = 4;

/** Below this score we do not name an industry. At this score the guess is still weak. */
export const MIN_SCORE_TO_DETECT = 3;
/** A named industry at or above this score is strong enough to proceed into questions. */
export const STRONG_MIN_SCORE = 6;

export const INDUSTRY_SIGNALS = {
  homeServices: [
    { keyword: 'hvac', pattern: /\bhvac\b/gi, weight: 3 },
    { keyword: 'plumber', pattern: /\bplumb(?:er|ing)\b/gi, weight: 3 },
    { keyword: 'electrician', pattern: /\belectrician\b/gi, weight: 3 },
    { keyword: 'roofing', pattern: /\broof(?:ing|er)s?\b/gi, weight: 3 },
    { keyword: 'landscaping', pattern: /\blandscap(?:ing|er)\b/gi, weight: 2 },
    { keyword: 'pest control', pattern: /\bpest control\b/gi, weight: 3 },
    { keyword: 'home services', pattern: /\bhome services\b/gi, weight: 3 },
    { keyword: 'contractor', pattern: /\b(?:general )?contractor\b/gi, weight: 2 },
    { keyword: 'restoration', pattern: /\brestoration\b/gi, weight: 2 },
    { keyword: 'water heater', pattern: /\bwater heater\b/gi, weight: 3 },
    { keyword: 'garage door', pattern: /\bgarage door\b/gi, weight: 3 },
    { keyword: 'locksmith', pattern: /\blocksmith\b/gi, weight: 3 },
    { keyword: 'cleaning service', pattern: /\bcleaning service\b/gi, weight: 2 },
  ],
  legal: [
    { keyword: 'law firm', pattern: /\blaw firm\b/gi, weight: 4 },
    { keyword: 'attorney', pattern: /\battorneys?\b/gi, weight: 3 },
    { keyword: 'lawyer', pattern: /\blawyers?\b/gi, weight: 3 },
    { keyword: 'personal injury', pattern: /\bpersonal injury\b/gi, weight: 4 },
    { keyword: 'litigation', pattern: /\blitigation\b/gi, weight: 3 },
    { keyword: 'estate planning', pattern: /\bestate planning\b/gi, weight: 3 },
    { keyword: 'family law', pattern: /\bfamily law\b/gi, weight: 3 },
    { keyword: 'criminal defense', pattern: /\bcriminal defense\b/gi, weight: 3 },
    { keyword: 'practice areas', pattern: /\bpractice areas?\b/gi, weight: 2 },
    { keyword: 'legal', pattern: /\blegal (?:services|help|advice|team)\b/gi, weight: 2 },
  ],
  medicalDental: [
    { keyword: 'dentist', pattern: /\bdentists?\b/gi, weight: 4 },
    { keyword: 'dental', pattern: /\bdental\b/gi, weight: 3 },
    { keyword: 'orthodont', pattern: /\borthodont(?:ic|ics|ist)s?\b/gi, weight: 4 },
    { keyword: 'physician', pattern: /\bphysicians?\b/gi, weight: 3 },
    { keyword: 'patient', pattern: /\b(?:new )?patients?\b/gi, weight: 2 },
    { keyword: 'clinic', pattern: /\bclinic\b/gi, weight: 2 },
    { keyword: 'medical', pattern: /\bmedical (?:practice|office|center|group)\b/gi, weight: 3 },
    { keyword: 'hygienist', pattern: /\bhygienists?\b/gi, weight: 3 },
    { keyword: 'primary care', pattern: /\bprimary care\b/gi, weight: 3 },
    { keyword: 'accepting new patients', pattern: /\baccepting new patients\b/gi, weight: 3 },
  ],
  professionalServices: [
    { keyword: 'cpa', pattern: /\bcpa\b/gi, weight: 4 },
    { keyword: 'accountant', pattern: /\baccountants?\b/gi, weight: 3 },
    { keyword: 'bookkeeping', pattern: /\bbookkeep(?:ing|er)\b/gi, weight: 3 },
    { keyword: 'tax prep', pattern: /\btax (?:prep|preparation|return)s?\b/gi, weight: 3 },
    { keyword: 'consulting firm', pattern: /\bconsulting firm\b/gi, weight: 3 },
    { keyword: 'consultancy', pattern: /\bconsultanc(?:y|ies)\b/gi, weight: 3 },
    { keyword: 'boutique consultancy', pattern: /\bboutique consultanc(?:y|ies)\b/gi, weight: 4 },
    { keyword: 'advisory', pattern: /\badvisory\b/gi, weight: 2 },
    { keyword: 'management consulting', pattern: /\bmanagement consulting\b/gi, weight: 4 },
    { keyword: 'strategy consultant', pattern: /\bstrategy consultants?\b/gi, weight: 3 },
    { keyword: 'fractional c-suite', pattern: /\bfractional\s+(?:cfo|cmo|coo)\b/gi, weight: 4 },
    { keyword: 'business coach', pattern: /\bbusiness coach(?:es|ing)?\b/gi, weight: 3 },
    { keyword: 'operations consultant', pattern: /\boperations consultants?\b/gi, weight: 3 },
    { keyword: 'architect', pattern: /\barchitects?\b/gi, weight: 2 },
    { keyword: 'bookkeeper', pattern: /\bbookkeepers?\b/gi, weight: 3 },
  ],
  financialInsurance: [
    { keyword: 'financial advisor', pattern: /\bfinancial advisors?\b/gi, weight: 4 },
    { keyword: 'financial planner', pattern: /\bfinancial planners?\b/gi, weight: 4 },
    { keyword: 'financial planning', pattern: /\bfinancial planning\b/gi, weight: 3 },
    { keyword: 'wealth management', pattern: /\bwealth management\b/gi, weight: 4 },
    { keyword: 'insurance agency', pattern: /\binsurance agenc(?:y|ies)\b/gi, weight: 4 },
    { keyword: 'insurance broker', pattern: /\binsurance brokers?\b/gi, weight: 3 },
    { keyword: 'life insurance', pattern: /\blife insurance\b/gi, weight: 3 },
    { keyword: 'fiduciary', pattern: /\bfiduciary\b/gi, weight: 3 },
    { keyword: 'registered investment', pattern: /\bregistered investment\b/gi, weight: 3 },
    { keyword: 'investment adviser', pattern: /\binvestment advis(?:er|or)s?\b/gi, weight: 3 },
    { keyword: 'series 7', pattern: /\bseries 7\b/gi, weight: 3 },
    { keyword: 'cfp', pattern: /\bcfp\b/gi, weight: 4 },
    { keyword: 'fee-only', pattern: /\bfee-only\b/gi, weight: 3 },
    { keyword: 'retirement planning', pattern: /\bretirement planning\b/gi, weight: 3 },
    { keyword: 'retirement income', pattern: /\bretirement income\b/gi, weight: 3 },
    { keyword: 'estate planning', pattern: /\bestate planning\b/gi, weight: 3 },
    { keyword: 'annuity', pattern: /\bannuit(?:y|ies)\b/gi, weight: 2 },
    { keyword: 'portfolio', pattern: /\bportfolios?\b/gi, weight: 2 },
    { keyword: 'planner', pattern: /\bplanners?\b/gi, weight: 2 },
    { keyword: 'first meeting', pattern: /\bfirst meeting\b/gi, weight: 2 },
  ],
  ecommerce: [
    { keyword: 'online store', pattern: /\bonline store\b/gi, weight: 4 },
    { keyword: 'add to cart', pattern: /\badd to cart\b/gi, weight: 4 },
    { keyword: 'shopify', pattern: /\bshopify\b/gi, weight: 3 },
    { keyword: 'woocommerce', pattern: /\bwoocommerce\b/gi, weight: 3 },
    { keyword: 'free shipping', pattern: /\bfree shipping\b/gi, weight: 3 },
    { keyword: 'sku', pattern: /\bskus?\b/gi, weight: 2 },
    { keyword: 'product catalog', pattern: /\bproduct catalog\b/gi, weight: 3 },
    { keyword: 'ecommerce', pattern: /\be-?commerce\b/gi, weight: 3 },
    { keyword: 'merchandise', pattern: /\bmerchandise\b/gi, weight: 2 },
  ],
  agencyMarketing: [
    { keyword: 'digital marketing agency', pattern: /\bdigital marketing agency\b/gi, weight: 4 },
    { keyword: 'marketing agency', pattern: /\bmarketing agency\b/gi, weight: 3 },
    { keyword: 'google partner', pattern: /\bgoogle partner\b/gi, weight: 3 },
    { keyword: 'google ads', pattern: /\bgoogle ads\b/gi, weight: 2 },
    { keyword: 'ppc', pattern: /\bppc\b/gi, weight: 2 },
    { keyword: 'seo', pattern: /\bseo\b/gi, weight: 2 },
    { keyword: 'paid search', pattern: /\bpaid search\b/gi, weight: 2 },
    { keyword: 'content marketing', pattern: /\bcontent marketing\b/gi, weight: 2 },
    { keyword: 'ai visibility', pattern: /\bai visibility\b/gi, weight: 3 },
    { keyword: 'performance marketing', pattern: /\bperformance marketing\b/gi, weight: 2 },
  ],
};

const SNIPPET_RADIUS = 50;
const MAX_DETECTION_EVIDENCE = 4;

function findMatches(text, re) {
  const flags = re.flags.includes('g') ? re.flags : `${re.flags}g`;
  const global = new RegExp(re.source, flags);
  const out = [];
  let m;
  while ((m = global.exec(text)) !== null) {
    out.push({ match: m[0], index: m.index });
    if (m[0].length === 0) global.lastIndex += 1;
  }
  return out;
}

function snippetAround(text, index, length) {
  const start = Math.max(0, index - SNIPPET_RADIUS);
  const end = Math.min(text.length, index + length + SNIPPET_RADIUS);
  let snippet = text.slice(start, end).replace(/\s+/g, ' ').trim();
  if (start > 0) snippet = `...${snippet}`;
  if (end < text.length) snippet = `${snippet}...`;
  return snippet;
}

function scoreIndustry(text, signals) {
  let score = 0;
  const evidence = [];
  const seen = new Set();

  for (const signal of signals) {
    const hits = findMatches(text, signal.pattern);
    if (hits.length === 0) continue;
    score += signal.weight;
    if (evidence.length >= MAX_DETECTION_EVIDENCE) continue;
    const hit = hits[0];
    const snippet = snippetAround(text, hit.index, hit.match.length);
    const key = `${signal.keyword}:${snippet}`;
    if (seen.has(key) || !snippet) continue;
    seen.add(key);
    evidence.push({ snippet, keyword: signal.keyword });
  }

  return { score, evidence };
}

const SCORED_INDUSTRIES = INDUSTRY_IDS.filter((id) => id !== 'generic');

export function industrySignalKeywords() {
  const out = {};
  for (const id of SCORED_INDUSTRIES) {
    out[id] = INDUSTRY_SIGNALS[id].map((s) => s.keyword);
  }
  return out;
}

function regulatedHitsFromScores(scored) {
  return scored
    .filter((row) => isRegulatedIndustryId(row.id) && row.score > 0)
    .map((row) => ({
      industry: row.id,
      label: INDUSTRY_LABELS[row.id],
      score: row.score,
      evidence: row.evidence,
    }));
}

function confidenceFor(score, fallback) {
  if (fallback || score < STRONG_MIN_SCORE) return 'weak';
  return 'strong';
}

/**
 * Detect a likely industry from KB text. Always states why — including
 * when the answer is "not enough signal" and the customer must pick.
 */
export function detectIndustry(kbContent) {
  const text = (kbContent || '').trim();
  if (!text) {
    return {
      industry: 'generic',
      label: INDUSTRY_LABELS.generic,
      fallback: true,
      confidence: 'weak',
      score: 0,
      regulatedHits: [],
      evidence: [
        {
          keyword: 'none',
          snippet: 'No knowledge base text was provided, so industry is unknown. Pick one so we ask the right questions.',
        },
      ],
    };
  }

  const scored = SCORED_INDUSTRIES.map((id) => {
    const result = scoreIndustry(text, INDUSTRY_SIGNALS[id]);
    return { id, ...result };
  }).sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return SCORED_INDUSTRIES.indexOf(a.id) - SCORED_INDUSTRIES.indexOf(b.id);
  });

  const best = scored[0];
  const runner = scored[1];
  const regulatedHits = regulatedHitsFromScores(scored);

  if (!best || best.score < MIN_SCORE_TO_DETECT) {
    const thinEvidence =
      best && best.score > 0 && best.evidence.length > 0
        ? best.evidence
        : [
            {
              keyword: 'none',
              snippet:
                'No strong industry signal in the knowledge base. We could not confidently tell what kind of business this is.',
            },
          ];
    return {
      industry: 'generic',
      label: INDUSTRY_LABELS.generic,
      fallback: true,
      confidence: 'weak',
      score: best?.score || 0,
      regulatedHits,
      evidence: thinEvidence,
    };
  }

  const detection = {
    industry: best.id,
    label: INDUSTRY_LABELS[best.id],
    fallback: false,
    confidence: confidenceFor(best.score, false),
    score: best.score,
    regulatedHits,
    evidence: best.evidence,
  };

  if (runner && runner.score >= MIN_SCORE_TO_DETECT && runner.score < best.score) {
    detection.runnerUp = { industry: runner.id, label: INDUSTRY_LABELS[runner.id] };
  }

  return detection;
}

export function questionApplies(gate, coverage) {
  if (gate === 'always') return true;
  if (gate === 'missingHighValuePage') {
    if (coverage.missingHighValuePages.length === 0) return false;
    return coverage.coverage.pricing.status === 'missing';
  }
  return coverage.coverage[gate]?.status === 'missing';
}

function sortByIdOrder(questions, order) {
  return [...questions].sort((a, b) => {
    const ai = order.indexOf(a.id);
    const bi = order.indexOf(b.id);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

/**
 * Essential + compliance first, then at least four pack preference
 * slots (when they apply), then helpful always-ask, then leftover preference.
 */
export function assembleQuestionSet(questions) {
  const seen = new Set();
  const unique = [];
  for (const question of questions) {
    if (seen.has(question.id) || questionKind(question) === 'hardRule') continue;
    seen.add(question.id);
    unique.push(question);
  }

  const compliance = [];
  const essential = [];
  const helpful = [];
  const preference = [];

  for (const question of unique) {
    if (questionKind(question) === 'compliance') {
      compliance.push(question);
    } else if (isEssentialAlwaysAskId(question.id)) {
      essential.push(question);
    } else if (isAlwaysAskId(question.id) || isHelpfulAlwaysAskId(question.id)) {
      helpful.push(question);
    } else {
      preference.push(question);
    }
  }

  const confirm = compliance.filter((q) => q.id === REGULATED_VERTICAL_CONFIRM_QUESTION.id);
  const otherCompliance = compliance.filter((q) => q.id !== REGULATED_VERTICAL_CONFIRM_QUESTION.id);
  const orderedEssential = sortByIdOrder(essential, ESSENTIAL_ALWAYS_ASK_IDS);
  const orderedHelpful = sortByIdOrder(helpful, HELPFUL_ALWAYS_ASK_IDS);
  const unskippable = [...confirm, ...orderedEssential, ...otherCompliance];
  const reserveCount = Math.min(
    RESERVED_PREFERENCE_SLOTS,
    preference.length,
    Math.max(0, MAX_QUESTIONS - unskippable.length)
  );
  const reserved = preference.slice(0, reserveCount);
  const leftoverPref = preference.slice(reserveCount);
  const core = [...unskippable, ...reserved];
  const helpfulRoom = Math.max(0, MAX_QUESTIONS - core.length);
  const helpfulKept = orderedHelpful.slice(0, helpfulRoom);
  const afterHelpful = [...core, ...helpfulKept];
  const extraRoom = Math.max(0, MAX_QUESTIONS - afterHelpful.length);
  return [...afterHelpful, ...leftoverPref.slice(0, extraRoom)];
}

function pickFromPack(packQuestions, coverage, usedIds) {
  const candidates = [];

  for (const question of packQuestions) {
    if (usedIds.has(question.id)) continue;
    if (questionKind(question) === 'hardRule') continue;

    if (questionKind(question) === 'compliance') {
      candidates.push(question);
      usedIds.add(question.id);
      continue;
    }

    if (!questionApplies(question.appliesWhenMissing, coverage)) continue;

    candidates.push(question);
    usedIds.add(question.id);
  }

  return assembleQuestionSet(candidates);
}

function complianceFromPack(industry) {
  return getIndustryPack(industry).questions.filter((q) => questionKind(q) === 'compliance');
}

/**
 * Choose 6–16 questions for the (detected or chosen) industry, skipping
 * anything the coverage scan already found in the KB.
 * If the pack is thin after skipping, fill from the generic pack.
 */
export function selectQuestions(input) {
  const detection = detectIndustry(input.kbContent);
  const chosen = input.chosenIndustry && INDUSTRY_IDS.includes(input.chosenIndustry)
    ? input.chosenIndustry
    : undefined;
  const industry = chosen || detection.industry;
  const industryWasChosen = Boolean(chosen);
  const requireIndustryPick = detection.confidence === 'weak' && !industryWasChosen;

  const usedIds = new Set();
  let questions = pickFromPack(getIndustryPack(industry).questions, input.coverage, usedIds);

  if (questions.length < MIN_QUESTIONS && industry !== 'generic') {
    const filler = pickFromPack(
      getIndustryPack('generic').questions,
      input.coverage,
      usedIds
    );
    questions = questions.concat(filler);
  }

  const unmatchedRegulated = detection.regulatedHits.filter((hit) => hit.industry !== industry);
  const needsConfirm = unmatchedRegulated.length > 0;
  if (needsConfirm && !usedIds.has(REGULATED_VERTICAL_CONFIRM_QUESTION.id)) {
    usedIds.add(REGULATED_VERTICAL_CONFIRM_QUESTION.id);
    questions = [REGULATED_VERTICAL_CONFIRM_QUESTION, ...questions];
  }

  const confirmed = isRegulatedIndustryId(input.regulatedConfirm || '')
    ? input.regulatedConfirm
    : undefined;
  if (confirmed && confirmed !== industry) {
    for (const question of complianceFromPack(confirmed)) {
      if (usedIds.has(question.id)) continue;
      usedIds.add(question.id);
      questions.push(question);
    }
  }

  questions = trimToCap(questions);

  const support = INDUSTRY_SUPPORT[industry];
  const restrictedFromConfirm = confirmed === 'financialInsurance';
  return {
    detection,
    industry,
    industryLabel: INDUSTRY_LABELS[industry],
    industryWasChosen,
    requireIndustryPick,
    support,
    restrictedNotice:
      support === 'restricted' || restrictedFromConfirm ? RESTRICTED_VERTICAL_NOTICE : undefined,
    questions,
    hardRules: HARD_RULES,
  };
}

function trimToCap(questions) {
  return assembleQuestionSet(questions);
}
